<template>
  <div>
    <v-card class="px-4 pt-3 pb-2 mx-0 mb-1 elevation-6">
      <div class="ledgersFiltersScroll">
        <v-row class="px-0 pt-4 pb-0 mx-0 d-flex flex-nowrap ledgersFiltersRow">
          <v-btn @click="find" color="secondary"
                 :disabled="disabledFind() || loadingDate || loading"
                 :loading="loading">
            <v-icon left>mdi-magnify</v-icon>
            Ledgers
          </v-btn>
          <span v-for="field in ledgerSearchFields" :key="field.val">
            <v-form v-model="searchValid">
              <date-picker-formatted :clearable="field.val === 'due'"
                                    :date-formatted-prop="field.val === 'due' ? '' : field.val === 'endDate' ? defaultEndDate : defaultStartDate"
                                    dense @enter="find"
                                    :format="dateFormat"
                                    :format-loosely-valid="dateFormatLooselyValid"
                                    :hint="['startDate','endDate'].includes(field.val) ? 'Reqd' : ''"
                                    :label="field.name"
                                    @loading="loadingDate = $event"
                                    menu-wrapper offset-y persistent-hint
                                    picker-color="secondary"
                                    prepend-inner-icon="mdi-calendar-month"
                                    :rules="field.val === 'due' ? [rules.date] : [rules.required, rules.date]"
                                    :textFieldClass="'mx-3 small mb-2'"
                                    :textFieldStyle="{width: field.width}"
                                    v-if="['startDate','endDate','due'].includes(field.val)"
                                    v-model="searches[field.val]" />
              <search-input class="mx-3 small"
                           :clearable="field.val !== 'dateType'"
                           dense @enter="find"
                           :hint="field.val === 'dateType' ? 'Reqd' : ''"
                           :items="field.select ? ['dateType','amt','paid','dep'].includes(field.val) ? ledgerItems[field.val] : $_lists[field.list] : []"
                           :item-text="field.val === 'cid' ? 'sort' : 'text'"
                           :item-value="field.val === 'cid' ? 'cid' : 'value'"
                           :label="field.name"
                           leave-x persistent-hint
                           :rules="field.val === 'dateType' ? [rules.required] : []"
                           :style="{width: field.width}"
                           :type="field.select ? 'VAutocomplete' : 'VTextField'"
                           v-else
                           v-model="searches[field.val]" />
            </v-form>
          </span>
        </v-row>
      </div>
    </v-card>

    <v-card px-0>
      <v-card-title class="mr-0 lowerLabel">
        <v-row dense class="mx-0 align-center">
          <v-col cols="12" sm="auto" class="px-0 pr-sm-6 d-flex align-center justify-start position-relative">
            <div class="subtitle-2 d-flex align-center flex-nowrap flex-grow-1">
              <span>
                <!-- 10 Aug 2026 JCH: NC-1625 — sum/rows follow visible table (results Search, or See Failed after bulk-save errors), not full loaded set -->
                <span class="d-inline d-md-none">Sum:</span>
                <span class="d-none d-md-inline">Sum of rows shown:</span>
                <b> {{ shownEntriesSum === null ? '' : `${$_curFormat(shownEntriesSum)}` }}</b>
              </span>
              <span class="ml-3">
                <span class="d-inline d-md-none">Rows:</span>
                <span class="d-none d-md-inline">Rows:</span>
                <b> {{ shownRowsCount ? shownRowsCount : '' }}</b>
              </span>
              <information-dialog-icon :confirm-class="'ml-2 ' + (isMobile ? 'mr-0' : 'mr-2') + ' title'"
                                      confirm-color="secondary darken-1"
                                      compact-body-on-xs
                                      :icon-class="'ml-2 mt-0'"
                                      tclass="headline grey lighten-2 mb-8"
                                      title="Ledgers Info">
              <div v-html="ledgersInfoHtml"></div>
            </information-dialog-icon>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on"
                       small text class="ml-1 px-2"
                       :disabled="loading || !virtualItems.length"
                       style="margin-top: 1px;"
                       @click="printLedgerResultsFrame">
                  <v-icon left small>mdi-printer</v-icon>
                  Print
                </v-btn>
              </template>
              <span>Print current results (filtered list) in a new window</span>
            </v-tooltip>
            </div>
          </v-col>
          <v-col cols="12" sm class="pl-0 pl-md-3 pr-md-2 d-flex align-center">
            <div class="d-sm-none mr-3 ml-0 pl-0" v-can="'ledger-edit'">
              <v-btn small @click="openEditDialog" color="secondary"
                     :disabled="!selectedRows.length"
                     style="margin-top: -2px; min-width: 65px; padding: 0 3px 0 6px;">
                <v-icon left>mdi-pencil</v-icon>
                <span style="margin-left: -6px;" v-if="selectedRows.length">({{ selectedRows.length }})</span>
              </v-btn>
            </div>
            <div class="d-none d-sm-inline mr-2" v-can="'ledger-edit'">
              <v-btn small @click="openEditDialog" color="secondary"
                     :disabled="!selectedRows.length"
                     style="margin-top: -2px; min-width: 80px; padding: 0 6px;" class="px-1">
                Edit{{ selectedRows.length ? ` (${selectedRows.length})` : '' }}
              </v-btn>
            </div>
            <div v-if="bulkErrorRows.size > 0" class="d-flex ml-1" v-can="'ledger-edit'">
              <v-btn small outlined color="error"
                     @click="showOnlyErrors = !showOnlyErrors"
                     :class="['mr-2', showOnlyErrors ? 'px-2' : 'px-1']">
                {{ showOnlyErrors ? 'See All' : 'See Failed' }}
              </v-btn>
              <v-btn small outlined color="error"
                     @click="clearBulkErrors"
                     :icon="isMobile" class="px-2 mr-2">
                <v-icon v-if="isMobile">mdi-close-circle</v-icon>
                <span v-else>Clear Alerts</span>
              </v-btn>
            </div>
            <v-spacer/>
            <v-text-field v-model="search"
                         append-icon="mdi-magnify"
                         :placeholder="isMobile ? '' : 'Search'"
                         :hint="(ledger && ledger.length) ? `Results: ${virtualItems.length.toLocaleString()}/${ledger.length.toLocaleString()}` : ''"
                         persistent-hint
                         clearable
                         @click:clear="search = ''"
                         style="min-width: 150px;"
                         dense
                         class="flex-grow-1" />
          </v-col>
        </v-row>
      </v-card-title>
    </v-card>

    <v-card>
      <div class="ledgersTableScrollContainer">
      <div class="pb-1 mb-1 elevation-2 secondary-icon ledgersVirtualTable" :style="$_cssVars">
          <div ref="ledgersVirtualHeader" class="ledgersVirtualHeader">
            <div class="ledgersVirtualHeaderRow d-flex flex-nowrap align-center" style="min-width: fit-content;">
            <div v-if="$_can('ledger-edit')" class="ledgersCell ledgersSelectCell">
              <v-simple-checkbox
                :indeterminate="someVisibleRowsSelected && !allRowsSelected"
                :value="allRowsSelected"
                @click.stop="onHeaderCheckboxClick($event)"
              />
            </div>
              <div v-for="h in colHeaders" :key="h.value"
                   class="ledgersCell ledgersHeaderCell"
                   :class="h.align === 'end' ? 'd-flex justify-end align-center' : ''"
                   :style="h.align === 'end' ? { width: h && h.width ? h.width : undefined } : cellStyle(h)"
                   @click="toggleSort(h)">
              <span>{{ h.text }}</span>
                <v-icon v-if="sortBy.includes(h.value)" class="ml-1" small>
                {{ sortDesc[sortBy.indexOf(h.value)] ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
              </v-icon>
            </div>
          </div>
        </div>
        <v-virtual-scroll v-if="virtualItems.length > 0"
          ref="ledgersVirtualScroll"
          class="ledgersVirtualBody"
          :class="{ 'ledgersVirtualBody--hidden': !showTableRows }"
          :items="virtualItems"
          :height="virtualTableHeight"
          :item-height="virtualRowHeight"
          :bench="10"
                           :key="virtualScrollKey">
          <template v-slot:default="{ item, index }">
              <div class="ledgersVirtualRow d-flex flex-nowrap align-center"
                   :class="{ ledgersVirtualRowAlt: index % 2 === 1,
                            ledgersRowSelected: isSelected(item) && !isEditingRow(item),
                            ledgersRowEditing: isEditingRow(item),
                            ledgersRowError: bulkErrorRows.has(item.lid) }"
                   :style="bulkErrorRows.has(item.lid) ? { position: 'relative' } : {}"
                   @click="rowClick(item)">
              <div v-if="$_can('ledger-edit')" class="ledgersCell ledgersSelectCell" @click.stop="onCellClick($event, item)">
                <v-simple-checkbox :value="isSelected(item)" @click.stop="onRowCheckboxClick($event, item)" />
              </div>
                <div v-for="h in colHeaders" :key="`${item.lid}-${h.value}`"
                     class="ledgersCell ledgersBodyCell"
                     :style="cellStyle(h)">
                <template v-if="h.value === 'actions'">
                  <div class="ledgersActionsCell" @click.stop>
                      <button-tip v-can="'ledger-edit'"
                                 @click="openRowEditDialog(item)"
                                 icolor="secondary darken-1"
                                 icon iname="mdi-pencil-outline"
                                 small
                                 :tip="'Edit Ledger'"
                                 :open-delay="500" />
                      <button-tip @click="$_editFirm(item.firms_fid)"
                                 icolor="secondary darken-1"
                                 icon iname="mdi-account-multiple-outline"
                                 small
                                 :tip="'Edit Firm'"
                                 v-can="'firm-edit'"
                                 :open-delay="600"/>
                      <button-tip @click="$_editPolicy(item.pid)"
                                 :disabled="!item.pid"
                                 icolor="secondary darken-1"
                                 icon iname="mdi-file-document-outline"
                                 small
                                 :tip="item.pid ? 'Edit Policy' : 'Policy id missing'"
                                 v-can="'policy-edit'"
                                 :open-delay="600"/>
                      <v-menu v-if="bulkErrorRows.has(item.lid)"
                             offset-y close-on-click
                             :max-width="$vuetify.breakpoint.xs ? '90vw' : '400px'"
                             :min-width="$vuetify.breakpoint.xs ? '280px' : '350px'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon x-small @click.stop style="margin-left: 2px;">
                          <v-icon size="16" style="color: #b71c1c;">mdi-alert-circle</v-icon>
                        </v-btn>
                      </template>
                      <v-card class="pa-0" style="max-height: 300px; overflow-y: auto;">
                        <v-card-text class="pa-3">
                          <div class="error-details pl-3 mb-2">
                            <strong style="color: #b71c1c;">Save failed:</strong> {{ bulkErrorDetails.get(item.lid) || 'Unknown error' }}
                          </div>
                          <v-divider class="my-2"></v-divider>
                          <div class="pl-2 d-flex align-center">
                            <v-icon small color="primary" class="mr-2">mdi-pencil</v-icon>
                            <span class="body-2">Click edit button to fix/retry</span>
                          </div>
                        </v-card-text>
                        <v-card-actions class="pl-2 pr-2 pt-0">
                          <v-btn small text @click="clearSingleError(item.lid)">
                            <v-icon left small>mdi-close-circle</v-icon>
                            Clear
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                  </div>
                </template>
                <template v-else-if="h.value === 'firms_sort'">
                  <v-tooltip bottom open-delay="300" close-delay="150"
                    :open-on-hover="false" :open-on-focus="false"
                    :value="activeFirmSortTooltipLid === item.lid">
                      <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="firmSortTooltipOn(on, item)" class="ledgersFirmSortText">{{ item.firms_sort || '' }}</span>
                      </template>
                      <span>{{ item.firms_sort || '' }}</span>
                    </v-tooltip>
                </template>
                <template v-else-if="h.value === 'amt'">
                  {{ $_curFormat(item.amt) }}
                </template>
                <template v-else-if="h.value === 'ledgers_note'">
                  <span>{{ truncateText(getNoteText(item), 60) }}</span>
                </template>
                <template v-else>
                  {{ item[h.value] }}
                </template>
              </div>
            </div>
          </template>
        </v-virtual-scroll>
        </div>
      </div>
      <div v-if="!loading && virtualItems.length === 0"
           class="ledgersNoResults d-flex align-center justify-center"
           :style="{ minHeight: isMobile ? '300px' : virtualTableHeight + 'px',
                    height: isMobile ? 'auto' : virtualTableHeight + 'px' }">
        <div :class="['text-center grey--text', isMobile ? 'body-2' : 'body-1']">No data. Check search fields and click 🔍 LEDGERS.</div>
      </div>
  </v-card>
    <v-dialog
      v-model="editDialogOpen"
      max-width="620px"
      persistent
      :fullscreen="$vuetify && $vuetify.breakpoint && $vuetify.breakpoint.xs"
      :transition="$vuetify && $vuetify.breakpoint && $vuetify.breakpoint.xs ? 'dialog-bottom-transition' : 'dialog-transition'"
      style="z-index: 1500;"
    >
      <v-card>
        <v-card-title :class="['pt-3 pt-sm-5 pl-2 pl-sm-6', editDialogMode === 'bulk' ? 'pr-0 pr-sm-8' : 'pr-2 pr-sm-8']" :style="isMobile ? 'display: block;' : 'd-flex align-center'">
          <div :class="isMobile ? 'd-flex justify-space-between align-center pt-2 pb-1' : ''">
            <span :class="isMobile ? 'headline ml-0.5' : 'headline'" :style="isMobile ? 'margin-top: 3px' : ''">
              {{ editDialogMode === 'bulk' ? 'Edit Ledgers' : 'Edit Ledger' }}
            </span>
            <span class="subtitle-2 text-right mr-2 mt-2" v-if="isMobile && editDialogMode === 'bulk'">Selected: <b>{{ selectedRows.length }}</b></span>
          </div>
          <v-spacer v-if="!isMobile && editDialogMode === 'bulk'"/>
          <span :class="'subtitle-2 mt-2'" v-if="!isMobile && editDialogMode === 'bulk'">Selected: <b>{{ selectedRows.length }}</b></span>
          <div v-if="editDialogTargetRow" class="d-flex align-center ml-auto">
            <span class="subtitle-2">
              <span>LID: </span><span><b>{{ editDialogTargetRow.lid }}</b></span>
              <span v-if="editDialogTargetRow.firms_producer" class="ml-3">Producer: <b>{{ editDialogTargetRow.firms_producer }}</b></span>
              <span v-if="editDialogTargetRow.cid_sort" class="ml-3">Entity: <b>{{ editDialogTargetRow.cid_sort }}</b></span>
            </span>
          </div>
        </v-card-title>
        <v-card-text :class="['pt-3', 'pt-sm-6', 'pb-0', isMobile ? 'px-4' : 'px-12']">
          <v-form v-model="editDialogValid">
            <div v-if="editDialogMode === 'single'">
              <v-row dense>
                <v-col v-for="f in editDialogFieldDefs" :key="f.key" cols="12">
                  <component
                    :is="dialogFieldComponent(f)"
                    :class="dialogFieldClass(f, 'single')"
                    v-bind="dialogFieldProps(f, 'single')"
                    v-model="editDialogRow[f.key]"
                  />
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <v-row dense :class="isMobile ? 'mt-n4' : ''">
                <v-col cols="12" class="d-flex flex-nowrap align-center mb-1">
                  <v-checkbox
                    class="mr-2 mt-0"
                    color="secondary"
                    hide-details
                    :input-value="allBulkApply"
                    @click.stop.prevent="toggleAllBulkApply"
                  />
                  <span class="subtitle-1 font-weight-medium" @click="toggleAllBulkApply">Select all fields</span>
                </v-col>
              </v-row>
              <v-row
                v-for="f in editDialogFieldDefs"
                :key="f.key"
                dense
                :class="bulkFieldRowClass(f)"
              >
                <v-col cols="12" :class="bulkFieldColClass(f)">
                  <v-checkbox
                    class="mr-2 mt-0"
                    color="secondary"
                    hide-details
                    :input-value="!!editDialogBulkApply[f.key]"
                    @click.stop.prevent="onBulkApplyCheckboxClick($event, f.key)"
                  />
                  <component
                    :is="dialogFieldComponent(f)"
                    :class="dialogFieldClass(f, 'bulk')"
                    v-bind="dialogFieldProps(f, 'bulk')"
                    v-model="editDialogBulk[f.key]"
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions :class="['pt-0', 'mt-n3', isMobile ? 'px-4' : 'px-8']">
          <div v-if="editDialogProgressTotal" class="d-flex flex-column mr-3">
            <div :class="[isMobile ? 'caption font-weight-medium' : 'body-2']" :style="isMobile ? 'font-size: 0.75rem !important; line-height: 1.2;' : ''">
              Saving <b>{{ editDialogProgressCurrent }}</b> / <b>{{ editDialogProgressTotal }}</b><span v-if="editDialogProgressErrors"> / <span class="red--text"><b>{{ editDialogProgressErrors }}</b></span></span>
            </div>
            <v-progress-linear
              class="mt-1"
              :style="{ width: isMobile ? '100px' : '300px', maxWidth: isMobile ? '100px' : '300px' }"
              color="secondary"
              :value="Math.round((editDialogProgressCurrent / editDialogProgressTotal) * 100)"
            />
          </div>
          <v-spacer/>
          <v-btn text @click="closeEditDialog" :disabled="editDialogSaving">Cancel</v-btn>
          <v-btn color="secondary" @click="saveEditDialog" :loading="editDialogSaving" :disabled="!editDialogValid">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <snack :color="$_snack.color" :msg="$_snack.msg" :timeout="$_snack.timeout" v-model="$_snack.open" v-if="$_snack.cmpnt==='LedgersSearch'" style="z-index: 1600;"></snack>
  </div>
</template>
<script>
import { LedgersAPIService } from "@/servicehandlers/LedgersAPIService";
import currency from 'currency.js';// 12 May JCH: summed amounts in ledger print popup
import moment from "moment";
const ledgersAPIService = new LedgersAPIService();
const EDIT_DIALOG_FIELDS = Object.freeze([{ key: 'num', type: 'smart', label: 'Ref', bulkLabel: 'Ref (apply to all)' }, { key: 'amt', type: 'smart', label: 'Amount', bulkLabel: 'Amount (apply to all)', rules: 'amt' }, { key: 'due', type: 'date', label: 'Due', bulkLabel: 'Due (apply to all)' }, { key: 'paid', type: 'date', label: 'Paid', bulkLabel: 'Paid (apply to all)' }, { key: 'dep', type: 'date', label: 'Deposited', bulkLabel: 'Deposited (apply to all)' }, { key: 'ledgers_note', type: 'textarea', label: 'Note', bulkLabel: 'Note (apply to all)', bulkRowClass: 'mt-4' }]);
// 12 May JCH: print col % (sum 100); paid/dep sized for MM-DD-YYYY without wrap; slice from Note.
const LEDGER_PRINT_COL_WIDTH_PCT = Object.freeze({
  firms_fid: 5,
  firms_sort: 17,
  policies_expiry: 7,
  firms_producer: 5.5,
  cid_sort: 6.5,
  ledgers_type: 7,
  acct: 5,
  num: 5,
  amt: 7.5,
  due: 7,
  paid: 7,
  dep: 7,
  ledgers_note: 13.5,
});
// ~two wrapped lines at print 9px / line-height 1.25 plus ~6px vertical cell padding (Option A row height).
const LEDGER_PRINT_BODY_CELL_MIN_HEIGHT = '31px';
export default {
  name: "LedgersSearch",
  data() {
    return {
      loading: false, loadingDate: false, searchValid: false,
      colHeaders: [{value:'actions',text:'Edit',align:'center',width:'120px',sortable:false},{value:'firms_fid',text:'FID',align:'end',width:'120px'},{value:'firms_sort',text:'Sort',align:'start',width:'226px'},{value:'policies_expiry',text:'Expiry',align:'start',width:'110px'},{value:'firms_producer',text:'Producer',align:'start',width:'110px'},{value:'cid_sort',text:'Entity',align:'start',width:'100px'},{value:'ledgers_type',text:'Type',align:'start',width:'118px'},{value:'acct',text:'Acct',align:'start',width:'118px'},{value:'num',text:'Ref',align:'start',width:'120px'},{value:'amt',text:'Amount',align:'end',width:'110px'},{value:'due',text:'Due',align:'start',width:'110px'},{value:'paid',text:'Paid',align:'start',width:'110px'},{value:'dep',text:'Deposited',align:'start',width:'110px'},{value:'ledgers_note',text:'Note',align:'start',width:'300px'}],
      dateFormat: 'MM-DD-YYYY', dateFormatLooselyValid: 'M-D-YYYY',// for DatePickerFormatted.vue to accept and add leading zeros
      editFields: ['num', 'amt', 'due', 'paid', 'dep', 'ledgers_note'],
      ledger: [],// copy of ledger used for display in table
      ledgerItems: [],// dropdown items
      maxSearchResults: 1000,// 1 Jan 2026 JCH: more rows slows UI - backend also currently limits to 1000
      rules: { amt: (x) => /^[-]?\d*(\.\d{0,2})?$/.test(x) || 'Not a valid amount', date: (x) => (!x || moment(x, 'MM-DD-YYYY', true).isValid()) || 'Only MM-DD-YYYY', required: (x) => !!x || 'Reqd' },
      searches: {},// search values for ledgersSearch API
      search: '', selectedRows: [],// rows selected in the data table (for dialog editing)
      sortBy: ['due', 'ledgers_type'], sortDesc: [true, false],// mirrors previous default v-data-table sort-desc
      total: '', virtualTableHeight: 640, virtualRowHeight: 32,
      lastSelectedIndex: null, activeFirmSortTooltipLid: null,
      bulkErrorRows: new Set(), bulkErrorDetails: new Map(), // lid -> error message
      showOnlyErrors: false, preserveErrorHighlighting: false, // Flag to preserve error highlighting across searches
      tempSearchBackup: undefined, // Temporary storage for search when showing error rows
      showTableRows: true, // Hide rows during error transitions
      scrollThrottleTimer: null, virtualScrollKey: 0, // Force virtual scroll re-render when needed
      editDialogMode: 'bulk', // 'single' | 'bulk'
      editDialogOpen: false, editDialogValid: true, editDialogSaving: false,
      editDialogRow: { num: '', amt: '', due: '', paid: '', dep: '', ledgers_note: '' },
      editDialogRowOriginal: null, editDialogTargetRow: null,
      editDialogBulk: { num: '', amt: '', due: '', paid: '', dep: '', ledgers_note: '' },
      editDialogBulkApply: { num: false, amt: false, due: false, paid: false, dep: false, ledgers_note: false },
      editDialogProgressCurrent: 0, editDialogProgressTotal: 0, editDialogProgressErrors: 0,
      lastBulkApplyKey: null,
    }
  },
  computed: {
    isMobile() { return this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.xs; },
    ledgersInfoHtml() {
      const max = this.maxSearchResults;
      const canEdit = typeof this.$_can === 'function' ? this.$_can('ledger-edit') : false;
      let html = `<div style="margin-top: -4px;"></div>
        <b>🔍 Search Ledgers:</b>
        <div style="margin-left: 20px;">
          Enter start/end dates + type, click 🔍 LEDGERS or press return<br>
          Dates auto-format to <span style="color: green">MM-DD-YYYY</span><br>
          Click 🔍 LEDGERS to refresh anytime, max ${max} results - narrow if needed<br>
          <b>Print</b> opens the filtered rows in a new window for your system print dialog (allow pop-ups if blocked).
        </div>
        <div style="margin-bottom: 2px;"></div>`;

      if (canEdit) {
        html += `<div style="margin-top: 10px;"></div>
          <b>✏️ Editing:</b>
          <div style="margin-left: 20px;">
            Click <i class="mdi mdi-pencil" style="font-size: 14px;"></i> to edit single rows<br>
            Click <i class="mdi mdi-checkbox-outline" style="font-size: 14px;"></i> checkboxes/rows to bulk edit<br>
            Shift+click range.<br>
            Header checkbox toggles all.<br>
            New search clears selection
          </div>
          <div style="margin-top: 10px;"></div>
          <b>⚠️ Error Handling:</b>
          <div style="margin-left: 20px;">
            Rows with failed saves show <span style="color: #b71c1c">red text</span><br>
            Click <i class="mdi mdi-alert-circle" style="color: #b71c1c; font-size: 14px;"></i> red circle to see error details<br>
            Click <i class="mdi mdi-close-circle" style="color: #b71c1c; font-size: 14px;"></i> or Clear Alerts to clear all alerts
          </div>
          <div style="margin-bottom: -2px;"></div>`;
      }
      return html;
    },
    allBulkApply: {
      get() {
        const a = this.editDialogBulkApply;
        return a.num && a.amt && a.due && a.paid && a.dep && a.ledgers_note;
      },
      set(v) {
        this.editDialogBulkApply = { num: v, amt: v, due: v, paid: v, dep: v, ledgers_note: v };
      }
    },
    editDialogFieldDefs() { return EDIT_DIALOG_FIELDS; },
    selectedLids() { return new Set((this.selectedRows || []).map(x => x.lid)); },
    allRowsSelected() {
      if (!this.$_can('ledger-edit')) return false;
      if (!this.virtualItems.length) return false;
      for (let row of this.virtualItems) if (!this.selectedLids.has(row.lid)) return false;
      return true;
    },
    // 12 May 2026 JCH: True when at least one visible row is selected (used with allRowsSelected for header indeterminate).
    someVisibleRowsSelected() {
      if (!this.$_can('ledger-edit')) return false;
      if (!this.virtualItems.length) return false;
      for (let row of this.virtualItems) {
        if (this.selectedLids.has(row.lid)) return true;
      }
      return false;
    },
    virtualItems() {
      let items = this.ledger || [];
      let s = (this.search || '').toString().trim().toLowerCase();
      if (s) {
        items = items.filter((it) => this.getSearchBlob(it).includes(s));
      }
      if (this.showOnlyErrors && this.bulkErrorRows.size > 0) {
        items = items.filter(item => this.bulkErrorRows.has(item.lid));
        if (items.length === 0) {
          const allItems = this.ledger || [];
          items = allItems.filter(item => this.bulkErrorRows.has(item.lid));
        }
      }
      const sortBy = this.sortBy || [];
      const sortDesc = this.sortDesc || [];
      if (!sortBy.length) return items;
      const sorted = items.slice();
      sorted.sort((a, b) => {
        for (let i = 0; i < sortBy.length; i++) {
          const key = sortBy[i];
          if (!key || key === 'actions') continue;
          const desc = !!sortDesc[i];
          let av = this.getSortValCached(a, key);
          let bv = this.getSortValCached(b, key);
          if (av == null && bv == null) continue;
          if (av == null) return desc ? 1 : -1;
          if (bv == null) return desc ? -1 : 1;
          let cmp = 0;
          if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
          else cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
          if (cmp !== 0) return desc ? -cmp : cmp;
        }
        return 0;
      });
      return sorted;
    },
    // 10 Aug 2026 JCH: NC-1625 — Sum/Rows match visible rows: results Search, or See Failed
    // (bulk-edit button that filters the table to lids whose save failed).
    shownRowsCount() {
      return (this.virtualItems && this.virtualItems.length) || 0;
    },
    shownEntriesSum() {
      const rows = this.virtualItems || [];
      if (!rows.length) return null;
      let sum = currency(0);
      for (const row of rows) {
        if (!row || row.amt === null || row.amt === undefined || row.amt === '') continue;
        sum = sum.add(currency(row.amt));
      }
      return sum.value;
    },
    defaultEndDate() { return this.$_date(new Date()); },
    defaultStartDate() {
      const d = new Date();
      d.setDate(d.getDate() - 4);
      return this.$_date(d);
    },
    ledgerSearchFields() {
      return [{name: 'Entity', width: '149px', val: 'cid', select: true, list: 'cid'},{name: 'Start Date',width: '111px', val: 'startDate'},{name: 'End Date', width: '111px', val: 'endDate'},{name: 'Date Type', width: '110px', val: 'dateType', select: true},{name: 'Type', width: '150px', val: 'ledgers_type',select: true, list: 'ledgertype'},{name: 'Acct', width: '120px', val: 'acct', select: true, list: 'ledgeracct'},{name: 'Ref', width: '124px', val: 'num'},{name: 'Amount', width: '128px', val: 'amt', select: true},{name: 'Due', width: '136px', val: 'due'},{name: 'Paid', width: '110px', val: 'paid', select: true},{name: 'Deposited', width: '110px', val: 'dep', select: true}];
    },
  },
  created() { document.title = 'Ledgers'; this.searches =  {startDate: this.defaultStartDate, endDate: this.defaultEndDate, dateType: 'Due'}; this.ledgerItems['dateType'] = ['Due', 'Paid', 'Dep']; this.ledgerItems['amt'] = ['0 or more', '0 or less']; this.ledgerItems['paid'] = ['null', 'paid']; this.ledgerItems['dep'] = ['null', 'done']; }, mounted() { this.refreshVirtualLayout(); window.addEventListener('resize', this.onWindowResize); this.bindVirtualScrollSync(); }, beforeDestroy() { window.removeEventListener('resize', this.onWindowResize); this.unbindVirtualScrollSync(); },
  watch: {
    $_sideMenu() {
      this.refreshVirtualLayout(true); setTimeout(() => this.refreshVirtualLayout(true), 350);
    },
    editDialogBulk: {
      handler(newBulk) {
        if (!this.editDialogBulkApply) return;
        const bulkFields = ['num', 'amt', 'due', 'paid', 'dep', 'ledgers_note'];
        bulkFields.forEach(key => {
          const value = newBulk[key];
          if (value != null && value !== '' && !this.editDialogBulkApply[key]) {
            this.editDialogBulkApply[key] = true;
          }
        });
      },
      deep: true
    },
    virtualItems: {
      handler(newItems) {
        if (!newItems || !this.selectedRows.length) return;
        const visibleLids = new Set(newItems.map(x => x.lid));
        const selectedVisibleCount = this.selectedRows.filter(row => visibleLids.has(row.lid)).length;
        if (selectedVisibleCount < this.selectedRows.length) {
          this.selectedRows = [];
          this.lastSelectedIndex = null;
        }
      },
      immediate: false
    }
  },
  methods: {
    async devSim(kind) { // 9 Jan 2026 JCH: DEV SIMS (delete anytime) — enable with ?devsim=1
      if (process.env.NODE_ENV !== 'development') return;
      if (typeof window === 'undefined' || !window.location) return;
      if (!String(window.location.search || '').includes('devsim')) return;
      const delayMs = 100;
      if (delayMs > 0) await new Promise(r => setTimeout(r, delayMs));
      const rate = kind === 'single' ? 0.7 : 0.3;
      if (Math.random() < rate) throw new Error('Simulated save failure (dev).');
    },
    formatSaveError(e) {
      try {
        if (e && e.response && e.response.data) {
          if (e.response.data.message) return e.response.data.message;
          if (e.response.data.error) return e.response.data.error;
        }
        if (e && e.message) return e.message;
      } catch (x) {}
      return 'Unknown error occurred';
    },
    _dispatchClearSnack() {
      const store = this.$store;
      if (!store || !store._actions || !store._actions.CLEAR_SNACK) return;
      store.dispatch('CLEAR_SNACK', { cmpnt: 'LedgersSearch' });
    },
    toggleErrorFilter() {
      this._dispatchClearSnack();
      this.showOnlyErrors = !this.showOnlyErrors;
      if (this.showOnlyErrors && this.bulkErrorRows.size > 0) {
        const currentItems = this.virtualItems;
        const visibleErrors = currentItems.filter(item => this.bulkErrorRows.has(item.lid)).length;
        if (visibleErrors === 0) {
          this.tempSearchBackup = this.search;
          this.search = '';
        }
      } else if (!this.showOnlyErrors && this.tempSearchBackup !== undefined) {
        this.search = this.tempSearchBackup;
        this.tempSearchBackup = undefined;
      }
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    clearBulkErrors() {
      this._dispatchClearSnack();
      this.bulkErrorRows = new Set();
      this.bulkErrorDetails = new Map();
      this.showOnlyErrors = false;
      this.preserveErrorHighlighting = false;
      this.virtualScrollKey = Date.now();
      this.$forceUpdate();
    },
    clearSingleError(lid) {
      this._dispatchClearSnack();
      this.bulkErrorRows.delete(lid);
      this.bulkErrorDetails.delete(lid);
      if (this.bulkErrorRows.size === 0) {
        this.showOnlyErrors = false;
        this.preserveErrorHighlighting = false;
      }
      this.virtualScrollKey = Date.now();
      this.$forceUpdate();
    },
    firmSortTooltipOn(on, item) {
      const lid = item && item.lid;
      return {
        ...on,
        click: () => {
          this.activeFirmSortTooltipLid = this.activeFirmSortTooltipLid === lid ? null : lid;
        },
        mouseenter: (e) => {
          const el = e && e.currentTarget;
          if (!el) return;
          if (el.scrollWidth > (el.clientWidth + 1)) this.activeFirmSortTooltipLid = lid;
        },
        focus: (e) => {
          const el = e && e.currentTarget;
          if (!el) return;
          if (el.scrollWidth > (el.clientWidth + 1)) this.activeFirmSortTooltipLid = lid;
        },
        mouseleave: () => { if (this.activeFirmSortTooltipLid === lid) this.activeFirmSortTooltipLid = null; },
        blur: () => { if (this.activeFirmSortTooltipLid === lid) this.activeFirmSortTooltipLid = null; },
      };
    },
    cellStyle(h) {
      const align = !h || !h.align ? 'left' : (h.align === 'end' ? 'right' : (h.align === 'center' ? 'center' : 'left'));
      return { width: h && h.width ? h.width : undefined, textAlign: align };
    },
    dialogFieldComponent(f) { return !f ? 'div' : (f.type === 'smart' ? 'smart-input' : (f.type === 'date' ? 'date-picker-formatted' : 'v-textarea')); },
    dialogFieldProps(f, mode) {
      if (!f) return {};
      const isBulk = mode === 'bulk';
      const label = isBulk ? (f.bulkLabel || f.label) : f.label;
      if (f.type === 'smart') {
        const props = { clearable: true, dense: true, label };
        if (f.rules === 'amt') {
          props.rules = isBulk
            ? (this.editDialogBulkApply[f.key] ? [this.rules.amt] : [])
            : [this.rules.amt];
        }
        return props;
      }
      if (f.type === 'date') {
        const model = isBulk ? this.editDialogBulk[f.key] : this.editDialogRow[f.key];
        const rules = isBulk
          ? (this.editDialogBulkApply[f.key] ? [this.rules.date] : [])
          : [this.rules.date];
        return {
          clearable: true,
          dense: true,
          menuWrapper: true,
          offsetY: true,
          dateFormattedProp: model,
          format: this.dateFormat,
          formatLooselyValid: this.dateFormatLooselyValid,
          rules,
          label,
          pickerColor: 'secondary',
        };
      }
      return { clearable: true, dense: true, label, rows: this.isMobile ? (isBulk ? 4 : 4) : 8 };
    },
    dialogFieldClass(f, mode) {
      if (!f) return '';
      const isBulk = mode === 'bulk';
      if (f.type === 'smart') return isBulk ? 'changedfield flex-grow-1' : 'changedfield';
      if (f.type === 'date') return 'flex-grow-1';
      return isBulk
        ? 'changedfield ledgersNoteTextarea ledgersBulkNoteTextarea flex-grow-1'
        : 'changedfield ledgersNoteTextarea mt-2';
    },
    bulkFieldRowClass(f) { return (f && f.bulkRowClass) ? f.bulkRowClass : ''; },
    bulkFieldColClass(f) { return `d-flex flex-nowrap ${f && f.type === 'textarea' ? 'align-start' : 'align-center'}`; },
    disabledFind() {// disable LEDGERS button if 3 search fields aren't filled and valid
      let s = this.searches;
      return !s.dateType || !this.searchValid;
    },
    find() {
      if (this.disabledFind() || this.isEndBeforeStart()) {
        let msg = ' Please try again. ';// if invalid field or startDate after endDate, tell user & return
        if (this.disabledFind()) msg += " Search field/s invalid. "
        if (this.isEndBeforeStart()) msg += " Is start date after end date? "
        this.$store.dispatch('SET_SNACK', {cmpnt: 'LedgersSearch', color: 'error', msg: msg, timeout: 15000, open: true});
        return;
      } else {
        this.selectedRows = [];
        this.lastSelectedIndex = null;
        this.lastBulkApplyKey = null;
        this.sortBy = ['due'];
        this.sortDesc = [true];
        if (this.preserveErrorHighlighting) {
          this.preserveErrorHighlighting = false; // Reset for next search
        } else {
          this.bulkErrorRows.clear();
          this.bulkErrorDetails.clear();
          this.showOnlyErrors = false;
        }
        this.loading = true;
        this.searches = _.omitBy(this.searches, (v) => _.isNil(v) || v === '');// remove null, undefined, ''
        ledgersAPIService.ledgersSearch(this.searches, this.$router).then((res) => {
          this.loading = false;
          let numFound = res.pagination.total;
          if (numFound > this.maxSearchResults) {
            this.$store.dispatch('SET_SNACK', { cmpnt: 'LedgersSearch', color: 'error',
              msg:`Refine search — ${(numFound || 0).toLocaleString()} found (max ${(this.maxSearchResults || 0).toLocaleString()})`, timeout: 15000, open: true});
            return;
          }
          // 12 May 2026 JCH: Reset inline Search box when new server rows load so old text does not hide the new result set.
          this.search = '';
          this.tempSearchBackup = undefined;
          this.total = res.total_amt;
          this.ledger = Object.freeze(res.results || []);
          if (this._noteTextCache) this._noteTextCache.clear();
          if (this._searchBlobCache) this._searchBlobCache.clear();
          if (this._sortValCache) this._sortValCache.clear();
          this.refreshVirtualLayout(true);
          this.scrollToTop();
        })
          .catch((error) => {
            this.loading = false;
            this.$store.dispatch('SET_SNACK', {cmpnt: 'LedgersSearch', color: 'error', msg: 'Search failed. Please check your dates/filters/connection or try again later.', timeout: 15000, open: true});
          });
      }
    },
    isEndBeforeStart() {// is the endDate before the startDate?
      let start = moment(this.searches.startDate, 'M-D-YYYY').format('YYYY-MM-DD');
      let end = moment(this.searches.endDate, 'M-D-YYYY').format('YYYY-MM-DD');
      let isEndBeforeStart = !moment(start).isSameOrBefore(end);
      return isEndBeforeStart;
    },
    isSelected(item) {
      if (!item) return false;
      return this.selectedLids.has(item.lid);
    },
    isEditingRow(item) {
      if (!item) return false;
      if (!this.editDialogOpen) return false;
      if (this.editDialogMode !== 'single') return false;
      const t = this.editDialogTargetRow;
      return !!t && item.lid === t.lid;
    },
    toggleRowSelected(item, checked) {
      if (!this.$_can('ledger-edit') || !item) return;
      const wantSelected = typeof checked === 'boolean' ? checked : !this.isSelected(item);
      if (wantSelected) {
        if (!this.isSelected(item)) this.selectedRows = [...this.selectedRows, item];
      } else {
        if (this.isSelected(item)) this.selectedRows = this.selectedRows.filter(x => x.lid !== item.lid);
      }
    },
    onHeaderCheckboxClick(e) {
      this.toggleSelectAll();
      this.lastSelectedIndex = null;
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
    },
    onCellClick(e, item) {
      if (e.target.closest('.v-simple-checkbox')) return;
      this.onRowCheckboxClick(e, item);
    },
    onRowCheckboxClick(e, item) {
      if (!this.$_can('ledger-edit') || !item) return;
      const items = this.virtualItems || [];
      const idx = items.findIndex(x => x && x.lid === item.lid);
      const wantSelected = !this.isSelected(item);
      if (e && e.shiftKey && this.lastSelectedIndex != null && this.lastSelectedIndex > -1 && idx > -1) {
        const start = Math.min(this.lastSelectedIndex, idx);
        const end = Math.max(this.lastSelectedIndex, idx);
        const range = items.slice(start, end + 1);
        const rangeLids = new Set(range.map(r => r.lid));
        if (wantSelected) {
          const existing = new Set(this.selectedRows.map(r => r.lid));
          const add = range.filter(r => !existing.has(r.lid));
          this.selectedRows = [...this.selectedRows, ...add];
        } else {
          this.selectedRows = this.selectedRows.filter(r => !rangeLids.has(r.lid));
        }
      } else {
        this.toggleRowSelected(item, wantSelected);
      }
      this.lastSelectedIndex = idx;
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
    },
    toggleSelectAll(checked) {
      if (!this.$_can('ledger-edit')) return;
      const wantSelected = typeof checked === 'boolean' ? checked : !this.allRowsSelected;
      const visibleLids = new Set(this.virtualItems.map(x => x.lid));
      if (!wantSelected) {
        this.selectedRows = this.selectedRows.filter(x => !visibleLids.has(x.lid));
        return;
      }
      const add = this.virtualItems.filter(x => !this.selectedLids.has(x.lid));
      this.selectedRows = [...this.selectedRows, ...add];
    },
    toggleSort(header) {
      if (!header || header.sortable === false) return;
      if (header.value === 'actions') return;
      const currentIndex = this.sortBy.indexOf(header.value);
      if (currentIndex > -1) {
        const isDescending = this.sortDesc[currentIndex];
        if (isDescending) {
          const newSortBy = [...this.sortBy];
          const newSortDesc = [...this.sortDesc];
          newSortBy.splice(currentIndex, 1);
          newSortDesc.splice(currentIndex, 1);
          this.sortBy = newSortBy;
          this.sortDesc = newSortDesc;
        } else {
          const newSortDesc = [...this.sortDesc];
          newSortDesc[currentIndex] = true;
          this.sortDesc = newSortDesc;
        }
      } else {
        this.sortBy = [...this.sortBy, header.value];
        this.sortDesc = [...this.sortDesc, false];
      }
    },
    rowClick(item) { this.toggleRowSelected(item); },
    noteText(note) {
      if (!note) return '';
      let s = String(note)
        .replace(/\r\n/g, '\n')
        .replace(/\uFFFD/g, ""); // Unicode replacement char; remove � from notes
      if (/<\/?[a-z][^>]*>/i.test(s)) s = s.replace(/<\/?[a-z][^>]*>/gi, '');
      return s.trim();
    },
    normalizeDialogDate(d) {
      if (!d) return '';
      const s = String(d);
      const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
      return m ? moment(m[1], 'YYYY-MM-DD').format('MM-DD-YYYY') : s;
    },
    normalizeDialogRowDates(row) {
      if (!row) return;
      row.due = this.normalizeDialogDate(row.due);
      row.paid = this.normalizeDialogDate(row.paid);
      row.dep = this.normalizeDialogDate(row.dep);
    },
    getNoteText(item) {// 31 Dec 2025 JCH: Cache computed note per row--avoid repeat regex work in render.
      if (!item) return '';
      const raw = item.ledgers_note || '';
      const key = `${item.lid}|${raw}`;
      if (!this._noteTextCache) this._noteTextCache = new Map();
      if (this._noteTextCache.has(key)) return this._noteTextCache.get(key);
      const val = this.noteText(raw);
      this._noteTextCache.set(key, val);
      return val;
    },
    printLedgerResultsFrame() {
      const rows = Array.isArray(this.virtualItems) ? this.virtualItems.slice() : [];
      if (!rows.length || this.loading) return;
      const cols = (this.colHeaders || []).filter((h) => h && h.value !== 'actions');
      const esc = (value) => {
        if (value == null || value === '') return '';
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
      const cellVal = (item, fieldVal) => {
        if (!item || !fieldVal) return '';
        if (fieldVal === 'amt') return esc(this.$_curFormat(item.amt));
        if (fieldVal === 'ledgers_note') return esc(this.getNoteText(item));
        return esc(item[fieldVal]);
      };
      const box = (h) => `text-align:${h && h.align === 'end' ? 'right' : 'left'};vertical-align:top;word-wrap:break-word;overflow-wrap:break-word`;
      const printedAt = moment().format('MMM D, YYYY h:mm A');
      const loadedCount = Array.isArray(this.ledger) ? this.ledger.length : rows.length;
      const filtered = loadedCount !== rows.length;
      let sumAmt = currency(0);
      for (const row of rows) {
        if (!row || row.amt === null || row.amt === undefined || row.amt === '') continue;
        sumAmt = sumAmt.add(currency(row.amt));
      }
      const printedSum = this.$_curFormat(sumAmt.value);
      let metaLines = `<div class="ledger-print-meta">${esc(printedAt)}<br/>
Rows printed: ${rows.length}`;
      if (filtered) metaLines += ` (filtered from ${loadedCount.toLocaleString()} loaded rows)`;
      metaLines += `<br/>
Sum (printed rows): ${esc(printedSum)}`;
      if (filtered && this.total !== '' && this.total != null && this.total !== undefined) {
        metaLines += `<br/>
Sum (all loaded rows, from server): ${esc(this.$_curFormat(this.total))}`;
      }
      metaLines += '</div>';
      const colgroup = '<colgroup>' + cols.map((h) =>
        `<col style="width:${LEDGER_PRINT_COL_WIDTH_PCT[h.value] ?? 4}%">`
      ).join('') + '</colgroup>';
      let thead = '<thead><tr>';
      for (const h of cols) thead += `<th style="${box(h)}">${esc(h.text)}</th>`;
      thead += '</tr></thead>';
      let tbody = '<tbody>';
      for (const item of rows) {
        tbody += '<tr>';
        for (const h of cols) tbody += `<td style="${box(h)}">${cellVal(item, h.value)}</td>`;
        tbody += '</tr>';
      }
      tbody += '</tbody>';
      const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>Ledgers</title>
<style>
  html,body{margin:0;padding:0}
  body{font-family:system-ui,-apple-system,sans-serif;font-size:10px;padding:4px 5px;color:#111;box-sizing:border-box}
  h1{font-size:13px;margin:0 0 4px;font-weight:600}
  .ledger-print-meta{margin:0 0 8px;line-height:1.4;font-size:10px}
  table{border-collapse:collapse;width:100%;max-width:100%;table-layout:fixed}
  th,td{border:1px solid #999;padding:3px 5px;font-size:9px;writing-mode:horizontal-tb;white-space:normal;line-height:1.25}
  th{background:#e8e8e8;font-weight:600}
  .ledger-print-table tbody tr{height:${LEDGER_PRINT_BODY_CELL_MIN_HEIGHT}}
  .ledger-print-table tbody td{vertical-align:top;box-sizing:border-box}
  .ledger-print-table tbody tr:nth-child(even){background:#f6f7f8}
  @media print{@page {size:landscape;margin-left:5mm;margin-right:5mm;margin-bottom:5mm;margin-top:9mm}@page :first {margin-top:5mm}body{padding:2px 4px}}
</style></head><body>
<h1>Ledgers results</h1>${metaLines}
<table class="ledger-print-table">${colgroup}${thead}${tbody}</table>
</body></html>`;
      const win = window.open('', `LedgerPrint_${Date.now()}`, 'width=1100,height=800,resizable=yes,scrollbars=yes');
      if (!win || !win.document) {
        this.$store.dispatch('SET_SNACK', {
          cmpnt: 'LedgersSearch',
          color: 'warning',
          open: true,
          msg: 'Unable to open print window. Allow pop-ups for this site.'
        });
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { try { win.print(); } catch (e) {} }, 250);
    },
    truncateText(text, maxLen) {
      if (!text) return '';
      const s = String(text);
      if (!maxLen || s.length <= maxLen) return s;
      return s.slice(0, Math.max(0, maxLen - 3)) + '...';
    },
    getSearchBlob(item) {
      if (!item) return '';
      if (!this._searchBlobCache) this._searchBlobCache = new Map();
      if (this._searchBlobCache.has(item)) return this._searchBlobCache.get(item);
      if (!this._searchBlobFields) {
        this._searchBlobFields = (this.colHeaders || [])
          .map(h => (h ? h.value : null))
          .filter(v => v && v !== 'actions');
      }
      const fields = this._searchBlobFields;
      let blob = '';
      for (let f of fields) {
        const v = item[f];
        if (v == null) continue;
        let text;
        if (f === 'ledgers_note') {
          text = this.noteText(v);
        } else if (f === 'amt') {
          const formatted = this.$_curFormat(v);
          const noSymbols = formatted.replace(/[$,]/g, ''); // Remove $ and commas only
          const withMinus = v < 0 ? '-' + Math.abs(v) : String(v);
          text = String(v) + ' ' + formatted + ' ' + noSymbols + ' ' + withMinus;
        } else {
          text = String(v).replace(/\uFFFD/g, "'");
        }
        blob += ' ' + text.toLowerCase();
      }
      blob = blob.trim();
      this._searchBlobCache.set(item, blob);
      return blob;
    },
    getSortValCached(item, key) {
      if (!item || !key) return null;
      if (!this._sortValCache) this._sortValCache = new Map();
      let perRow = this._sortValCache.get(item);
      if (!perRow) {
        perRow = Object.create(null);
        this._sortValCache.set(item, perRow);
      }
      if (Object.prototype.hasOwnProperty.call(perRow, key)) return perRow[key];
      let v = item[key];
      // 12 May JCH: FID sort - numeric when possible
      if (key === 'firms_fid') {
        if (v == null || v === '') {
          perRow[key] = null;
          return null;
        }
        const n = Number(v);
        perRow[key] = Number.isFinite(n) ? n : String(v);
        return perRow[key];
      }
      if (['due', 'paid', 'dep'].includes(key)) {
        const m = moment(v, this.dateFormat, true);
        v = m.isValid() ? m.valueOf() : null;
      }
      perRow[key] = v;
      return v;
    },
    refreshVirtualLayout(nextTick = false) {
      const run = () => {
        this.updateVirtualTableHeight();
      };
      if (nextTick) return this.$nextTick(run);
      return run();
    },
    scrollToTop() {
      this.$nextTick(() => {
        const virtualScroll = this.$refs.ledgersVirtualScroll;
        if (!virtualScroll) return;
        if (typeof virtualScroll.scrollToIndex === 'function') {
          virtualScroll.scrollToIndex(0);
          return;
        }
        const el = virtualScroll.$el || virtualScroll;
        if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: 0 });
        else if (el && typeof el.scrollTop === 'number') el.scrollTop = 0;
      });
    },
    getVirtualScrollEl() { const ref = this.$refs.ledgersVirtualScroll; return ref && ref.$el ? ref.$el : null; },
    onVirtualBodyScroll() {
      this.activeFirmSortTooltipLid = null;
      if (this.scrollThrottleTimer) return;
      this.scrollThrottleTimer = setTimeout(() => {
        this.scrollThrottleTimer = null;
      }, 16); // ~60fps
    },
    onWindowResize() {
      this.refreshVirtualLayout();
    },
    updateVirtualTableHeight() {
      const el = this.getVirtualScrollEl();
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const paddingBottom = this.isMobile ? 0 : 4;
      const viewportBottom = window.visualViewport
        ? Math.max(window.innerHeight, window.visualViewport.height + window.visualViewport.offsetTop)
        : window.innerHeight;
      const h = Math.floor(viewportBottom - rect.top - paddingBottom);
      this.virtualTableHeight = Math.max(200, h);
    },
    bindVirtualScrollSync() {
      const el = this.getVirtualTableScrollContainer();
      if (el) el.addEventListener('scroll', this.onVirtualBodyScroll, { passive: true });
      const vEl = this.getVirtualScrollEl();
      if (vEl) vEl.addEventListener('scroll', this.onVirtualBodyScroll, { passive: true });
      this.onVirtualBodyScroll();
      this.refreshVirtualLayout(true);
    },
    unbindVirtualScrollSync() {
      const el = this.getVirtualTableScrollContainer();
      if (el) el.removeEventListener('scroll', this.onVirtualBodyScroll);
      const vEl = this.getVirtualScrollEl();
      if (vEl) vEl.removeEventListener('scroll', this.onVirtualBodyScroll);
    },
    getVirtualTableScrollContainer() { return this.$el.querySelector('.ledgersTableScrollContainer'); },
    openEditDialog() {
      if (!this.$_can('ledger-edit') || !this.selectedRows.length) return;
      this.editDialogSaving = false;
      this.editDialogValid = true;
      this.editDialogProgressCurrent = 0;
      this.editDialogProgressTotal = 0;
      this.editDialogProgressErrors = 0;
      this.lastBulkApplyKey = null;
      this.bulkErrorRows.clear();
      this.bulkErrorDetails.clear();
      this.showOnlyErrors = false;
      if (this.selectedRows.length === 1) {
        this.editDialogMode = 'single';
        this.editDialogTargetRow = this.selectedRows[0];
        this.editDialogRowOriginal = this.selectedRows[0];
        this.editDialogRow = JSON.parse(JSON.stringify(this.selectedRows[0]));
        this.normalizeDialogRowDates(this.editDialogRow);
      } else {
        this.editDialogMode = 'bulk';
        this.editDialogTargetRow = null;
        this.editDialogRowOriginal = null;
        this.editDialogBulk = { num: '', amt: '', due: '', paid: '', dep: '', ledgers_note: '' };
        this.editDialogBulkApply = { num: false, amt: false, due: false, paid: false, dep: false, ledgers_note: false };
      }
      this.editDialogOpen = true;
    },
    openRowEditDialog(item) {
      if (!this.$_can('ledger-edit') || !item) return;
      this.editDialogMode = 'single';
      this.editDialogSaving = false;
      this.editDialogValid = true;
      this.editDialogProgressCurrent = 0;
      this.editDialogProgressTotal = 0;
      this.editDialogProgressErrors = 0;
      this.lastBulkApplyKey = null;
      this.editDialogTargetRow = item;
      this.editDialogRowOriginal = item;
      this.editDialogRow = JSON.parse(JSON.stringify(item));
      this.normalizeDialogRowDates(this.editDialogRow);
      this.editDialogOpen = true;
    },
    toggleAllBulkApply(e) {
      const want = !this.allBulkApply; // 31 Dec 2025 JCH: toggle all apply checkboxes
      this.editDialogBulkApply = { // 31 Dec 2025 JCH: replace object so checkboxes update in sync
        amt: want,
        ledgers_note: want,
        num: want,
        due: want,
        paid: want,
        dep: want,
      };
      this.lastBulkApplyKey = null;
      if (e && e.preventDefault) e.preventDefault();
    },
    onBulkApplyCheckboxClick(e, key) {
      if (!key || !this.editDialogBulkApply) return;
      const isShiftClick = e && e.shiftKey;
      const hasAnchor = this.lastBulkApplyKey;
      const isDifferentKey = this.lastBulkApplyKey !== key;
      if (isShiftClick && hasAnchor && isDifferentKey) {
        const order = ['num', 'amt', 'due', 'paid', 'dep', 'ledgers_note'];
        const a = order.indexOf(this.lastBulkApplyKey);
        const b = order.indexOf(key);
        if (a > -1 && b > -1) {
          const want = !this.editDialogBulkApply[key]; // desired state for clicked checkbox
          const start = Math.min(a, b);
          const end = Math.max(a, b);
          const newState = { ...this.editDialogBulkApply };
          for (let i = start; i <= end; i++) {
            const k = order[i];
            if (k in newState) newState[k] = want;
          }
          this.editDialogBulkApply = newState;
        }
        this.lastBulkApplyKey = key;
      } else {
        const want = !this.editDialogBulkApply[key];
        this.editDialogBulkApply = { ...this.editDialogBulkApply, [key]: want };
        this.lastBulkApplyKey = key;
      }
      if (e && e.preventDefault) e.preventDefault();
    },
    closeEditDialog() { if (!this.editDialogSaving) this.editDialogOpen = false; },
    buildUpdatePayloadFromRow(edited, original) {
      const payload = {};
      for (let f of this.editFields) {
        if (!edited || !original) continue;
        if (!this.$_eq(edited[f], original[f])) payload[f] = edited[f];
      }
      if (payload.ledgers_note !== undefined) {
        payload.note = payload.ledgers_note;
        delete payload.ledgers_note;
      }
      return payload;
    },
    buildUpdatePayloadFromBulk() {
      const payload = {};
      const bulkFields = ['num', 'amt', 'due', 'paid', 'dep', 'ledgers_note'];
      for (let f of bulkFields) {
        if (!this.editDialogBulkApply[f]) continue;
        payload[f] = this.editDialogBulk[f];
      }
      if (payload.ledgers_note !== undefined) {
        payload.note = payload.ledgers_note;
        delete payload.ledgers_note;
      }
      return payload;
    },
    async saveEditDialog() {
      if (!this.$_can('ledger-edit')) return;
      if (this.editDialogMode === 'bulk' && !this.selectedRows.length) return;
      this.editDialogSaving = true;
      try {
        if (this.editDialogMode === 'single') {
          const original = this.editDialogRowOriginal;
          const edited = this.editDialogRow;
          const payload = this.buildUpdatePayloadFromRow(edited, original);
          if (!Object.keys(payload).length) {
            this.$store.dispatch('SET_SNACK', { cmpnt: 'LedgersSearch', color: 'error',
              msg: 'No changes to save.', timeout: 6000, open: true});
            this.editDialogSaving = false;
            return;
          }
          try {
            await this.devSim('single');
          await ledgersAPIService.updateLedger(original.lid, payload, this.$router);
          } catch (singleError) {
            const errorMsg = this.formatSaveError(singleError);
            this.bulkErrorRows.add(original.lid);
            this.bulkErrorDetails.set(original.lid, errorMsg);
            this.$store.dispatch('SET_SNACK', {
              cmpnt: 'LedgersSearch',
              color: 'error',
              msg: this.isMobile
                ? `Save failed. Click red alert icon for details.`
                : `Save failed. Click red alert icon for details, click "See All" button for all rows.`,
              timeout: 15000,
              open: true
            });
            this.editDialogSaving = false;
            this.editDialogOpen = false; // 05 Jan 2026 JCH: Close dialog so user can hover over the error row
            return;
          }
        } else {
          this.showTableRows = true;
          const payload = this.buildUpdatePayloadFromBulk();
          if (!Object.keys(payload).length) {
            this.$store.dispatch('SET_SNACK', { cmpnt: 'LedgersSearch', color: 'error',
              msg: 'Choose at least one field to apply.', timeout: 6000, open: true});
            this.editDialogSaving = false;
            return;
          }
          const rows = this.selectedRows.slice();
          this.editDialogProgressTotal = rows.length;
          this.editDialogProgressCurrent = 0;
          this.editDialogProgressErrors = 0;
          const concurrency = Math.min(8, rows.length);
          let nextIndex = 0;
          const worker = async () => {
            while (nextIndex < rows.length) {
              const i = nextIndex++;
              const row = rows[i];
              try {
                await this.devSim('bulk');
                await ledgersAPIService.updateLedger(row.lid, payload, this.$router);
              } catch (e) {
                this.bulkErrorRows.add(row.lid);
                this.bulkErrorDetails.set(row.lid, this.formatSaveError(e));
                this.editDialogProgressErrors = (this.editDialogProgressErrors || 0) + 1;
              } finally {
                this.editDialogProgressCurrent = (this.editDialogProgressCurrent || 0) + 1;
              }
            }
          };
          await Promise.all(Array.from({ length: concurrency }, worker));
        }
        if (this.editDialogMode === 'single') {
        this.editDialogOpen = false;
          this.$store.dispatch('SET_SNACK', {
            cmpnt: 'LedgersSearch',
            color: 'secondary',
            msg: 'Ledger update saved.',
            timeout: 6000,
            open: true
          });
        } else {
          const successful = this.editDialogProgressTotal - this.editDialogProgressErrors;
        if (this.editDialogProgressErrors > 0) {
          this.showTableRows = false;
          setTimeout(() => {
            this.editDialogOpen = false;
            this.showOnlyErrors = true;
            this.preserveErrorHighlighting = true;
            this.showTableRows = true; // Show filtered error rows
            const msg = this.isMobile
              ? (successful > 0
                  ? `${successful} saved, ${this.editDialogProgressErrors} failed. Click red alert icon for details.`
                  : `${this.editDialogProgressErrors} failed. Click red alert icon for details.`)
              : (successful > 0
                  ? `${successful} saved, ${this.editDialogProgressErrors} failed. Click red alert icon for details, click "See All" button for all rows.`
                  : `${this.editDialogProgressErrors} failed. Click red alert icon for details, click "See All" button for all rows.`);
            this.$store.dispatch('SET_SNACK', {
              cmpnt: 'LedgersSearch',
              color: 'error',
              msg: msg,
              timeout: 15000,
              open: true
            });
        this.find();
          }, 2000); // 2 second delay to see completed progress bar
        } else {
          setTimeout(() => {
            this.editDialogOpen = false;
            this.$store.dispatch('SET_SNACK', {
              cmpnt: 'LedgersSearch',
              color: 'secondary',
              msg: `${this.editDialogProgressTotal} saved`,
              timeout: 6000,
              open: true
            });
            this.find(); // Refresh data for successful saves
          }, 2000); // 2 second delay to see completed progress bar
        }
        }
      } catch (e) {
        this.editDialogOpen = false; // 05 Jan 2026 JCH: Close dialog so user can see and hover over errors
        this.$store.dispatch('SET_SNACK', {
          cmpnt: 'LedgersSearch',
          color: 'error',
          msg: this.isMobile
            ? 'Bulk save failed. Click red alert icon for details.'
            : 'Bulk save failed. Click red alert icon for details, click "See All" button for all rows.',
          timeout: 15000,
          open: true
        });
      } finally {
        this.editDialogSaving = false;
      }
    },
  },
}
</script>
<style>
.secondary-icon .v-icon.v-icon { color: var(--v-secondary-darken1); }

.ledgersVirtualTable {
  display: inline-block;
  line-height: 1.5;
  min-width: 100%;
  background: #fff;
  color: rgba(0, 0, 0, 0.87);
  position: relative;
}

.ledgersVirtualHeader,
.ledgersVirtualBody,
.ledgersVirtualRow { box-sizing: border-box; }

.ledgersVirtualHeader,
.ledgersVirtualBody { width: 100%; }

.ledgersVirtualHeader {
  background: #fff;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.12);
  min-width: 100%;
}

.ledgersVirtualHeaderRow,
.ledgersVirtualRow,
.ledgersFiltersRow {
  width: fit-content;
  min-width: 100%;
}

.ledgersVirtualHeaderRow,
.ledgersVirtualRow {
  height: 32px; /* v-data-table--dense header/row height */
}

.ledgersTableScrollContainer {
  overflow-x: auto; /* Horizontal scrolling for entire table */
  overflow-y: hidden;
}

.ledgersVirtualBody {
  border-top: 0;
  overflow-x: hidden;
}

.ledgersVirtualBody--hidden {
  opacity: 0;
  pointer-events: none;
}

.ledgersNoResults {
  width: 100%;
  background: #fff;
}

.ledgersVirtualRow {
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
  cursor: pointer;
  background: #fff;
}

.ledgersVirtualRowAlt {
  background: #f5f7fa;
}

.ledgersVirtualRow:hover:not(.ledgersRowSelected):not(.ledgersRowEditing) { background: #eee !important; }

.ledgersRowSelected:not(.ledgersVirtualRowAlt) {
  background: #eceff1 !important;
} /* keep zebra-ish separation when odd rows selected */

.ledgersRowSelected.ledgersVirtualRowAlt {
  background: #dfe3e8 !important;
}

.ledgersRowEditing:not(.ledgersVirtualRowAlt) {
  background: #e0e0e0 !important;
} /* editing row darker than stripe + selected cues */

.ledgersRowEditing.ledgersVirtualRowAlt {
  background: #cfd4db !important;
}

.ledgersRowError {
  color: #b71c1c !important;
  font-weight: 500;
}

.ledgersRowError:hover { background: rgba(183, 28, 28, 0.04) !important; }

.ledgersCell {
  flex: 0 0 auto;
  padding: 0 16px; /* match v-data-table cell padding */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ledgersSelectCell { width: 44px; padding: 0 8px; }

.ledgersHeaderCell {
  cursor: pointer;
  user-select: none;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
}

.ledgersBodyCell { font-size: 0.875rem; user-select: text; }

.ledgersFirmSortText {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ledgersActionsCell {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.ledgersFiltersScroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  padding: 6px 0 0;
  min-height: 76px;
  box-sizing: border-box;
  touch-action: pan-x;
}

.ledgersNoteTextarea .v-label {
  z-index: 1;
  transform: translateY(-6px); /* slight lift to better match checkbox alignment */
  padding-bottom: 26px !important; /* more space under the label */
  background: var(--v-surface-base, #fff); /* match input/card background */
  left: 0 !important;
  right: 0 !important;
  width: 100% !important; /* extend across the full input width */
  max-width: none !important;
}

.ledgersNoteTextarea textarea {
  padding-top: 15px !important;
  padding-bottom: 0 !important;
  resize: none !important;
  max-height: 200px !important;
  border-top: 12px solid transparent !important;
  border-bottom: 8px solid transparent !important;
}

.ledgersBulkNoteTextarea { margin-top: 0 !important; }

.error-details {
  color: #b71c1c !important;
  font-weight: 500;
}

.error-tooltip-content {
  padding: 8px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  font-size: 14px;
  line-height: 1.4;
  color: #b71c1c;
  max-width: 300px;
}

.error-tooltip-content strong {
  color: #b71c1c;
  font-weight: 600;
}
</style>
